import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Globe, ArrowLeft, CheckCircle, Loader2, Eye, EyeOff, MapPin, Shield, Package, ArrowRight, Building, AlertCircle, Ship, ChevronDown, Search, FileText, User, Video } from 'lucide-react';
import { toast } from 'sonner';
import DistrictSelect from '@/components/DistrictSelect';
import PhoneVerification from '@/components/PhoneVerification';
import SEO, { SEO_CONFIGS } from '@/components/SEO';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState(resetToken ? 'reset-password' : 'role-select');
  
  // Multi-step registration state - Now 3 steps (removed video)
  const [registrationStep, setRegistrationStep] = useState(1); // 1: Basic, 2: Business ID, 3: Review
  
  // Country selection state
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryConfig, setCountryConfig] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    district: '',
    primary_business_id: '',
    secondary_business_id: '',
    phone: {
      countryCode: '+1',
      phoneNumber: '',
      verified: false
    }
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [countrySearch, setCountrySearch] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Filter countries based on search
  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Fetch supported countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axios.get(`${API}/seller-verification/countries`);
        setCountries(response.data.countries || []);
      } catch (error) {
        console.error('Failed to load countries:', error);
        toast.error('Failed to load countries');
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch country config when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setCountryConfig(null);
      return;
    }

    const fetchCountryConfig = async () => {
      setLoadingConfig(true);
      try {
        const response = await axios.get(`${API}/seller-verification/country/${selectedCountry.code}`);
        setCountryConfig(response.data);
        
        // Complete phone country codes for all 71 supported countries
        const countryPhoneCodes = {
          // Asia
          'IN': '+91', 'CN': '+86', 'JP': '+81', 'KR': '+82', 'VN': '+84',
          'ID': '+62', 'TH': '+66', 'MY': '+60', 'PH': '+63', 'SG': '+65',
          'BD': '+880', 'PK': '+92', 'LK': '+94', 'NP': '+977',
          // Middle East
          'AE': '+971', 'SA': '+966', 'QA': '+974', 'KW': '+965', 'OM': '+968',
          'BH': '+973', 'JO': '+962', 'IL': '+972', 'TR': '+90',
          // Africa
          'ZA': '+27', 'NG': '+234', 'KE': '+254', 'EG': '+20', 'GH': '+233',
          'TZ': '+255', 'UG': '+256', 'ET': '+251', 'MA': '+212', 'DZ': '+213',
          'TN': '+216', 'CI': '+225', 'SN': '+221', 'CM': '+237', 'RW': '+250',
          // Europe
          'GB': '+44', 'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34',
          'NL': '+31', 'BE': '+32', 'PL': '+48', 'SE': '+46', 'CH': '+41',
          'AT': '+43', 'PT': '+351', 'CZ': '+420', 'GR': '+30', 'RO': '+40',
          'HU': '+36', 'IE': '+353', 'DK': '+45', 'NO': '+47', 'FI': '+358',
          'RU': '+7', 'UA': '+380',
          // Americas
          'US': '+1', 'CA': '+1', 'MX': '+52', 'BR': '+55', 'AR': '+54',
          'CL': '+56', 'CO': '+57', 'PE': '+51',
          // Oceania
          'AU': '+61', 'NZ': '+64',
          // Default
          'OTHER': '+1'
        };
        setRegisterData(prev => ({
          ...prev,
          primary_business_id: '',
          secondary_business_id: '',
          phone: {
            ...prev.phone,
            countryCode: countryPhoneCodes[selectedCountry.code] || '+1'
          }
        }));
        setFieldErrors({});
      } catch (error) {
        console.error('Failed to load country config:', error);
        toast.error('Failed to load verification requirements');
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchCountryConfig();
  }, [selectedCountry]);

  // Validate business ID field
  const validateBusinessField = useCallback(async (fieldId, value) => {
    if (!selectedCountry || !value) {
      setFieldErrors(prev => ({ ...prev, [fieldId]: null }));
      return;
    }

    try {
      const response = await axios.post(
        `${API}/seller-verification/validate-field?country_code=${selectedCountry.code}&field_id=${fieldId}&value=${value}`
      );
      if (!response.data.valid) {
        setFieldErrors(prev => ({ ...prev, [fieldId]: response.data.message }));
      } else {
        setFieldErrors(prev => ({ ...prev, [fieldId]: null }));
      }
    } catch (error) {
      console.error('Validation error:', error);
    }
  }, [selectedCountry]);

  // Debounced field validation
  const handleBusinessFieldChange = (fieldId, value) => {
    const upperValue = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setRegisterData(prev => ({ ...prev, [fieldId]: upperValue }));
    
    // Debounce validation
    const timer = setTimeout(() => {
      validateBusinessField(fieldId, upperValue);
    }, 500);
    return () => clearTimeout(timer);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed';
      if (error.response?.status === 403 && errorDetail.includes('suspended')) {
        toast.error(errorDetail, { duration: 8000 });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate country selected
    if (!selectedCountry) {
      toast.error('Please select your country');
      return;
    }
    
    // Validate primary business ID if required
    const primaryField = countryConfig?.fields?.find(f => f.type === 'primary');
    if (primaryField?.required && !registerData.primary_business_id) {
      toast.error(`Please enter your ${primaryField.label}`);
      return;
    }
    
    // Check for validation errors
    if (Object.values(fieldErrors).some(err => err)) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    // Validate terms accepted
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Use to continue');
      return;
    }
    
    setIsLoading(true);

    try {
      // Build business IDs object based on country config
      const businessIds = {};
      if (primaryField && registerData.primary_business_id) {
        businessIds[primaryField.field_id] = registerData.primary_business_id;
      }
      const secondaryField = countryConfig?.fields?.find(f => f.type === 'secondary');
      if (secondaryField && registerData.secondary_business_id) {
        businessIds[secondaryField.field_id] = registerData.secondary_business_id;
      }

      const payload = {
        email: registerData.email,
        password: registerData.password,
        full_name: registerData.full_name,
        company_name: registerData.company_name,
        role: 'seller',
        country: selectedCountry.name,
        country_code: selectedCountry.code,
        phone: registerData.phone.phoneNumber ? 
          `${registerData.phone.countryCode}${registerData.phone.phoneNumber}` : null,
        phone_verified: registerData.phone.verified,
        district: selectedCountry.code === 'IN' ? registerData.district : null,
        business_ids: businessIds,
        verification_status: 'pending',
        terms_accepted: termsAccepted,
        terms_accepted_at: new Date().toISOString()
      };

      const response = await axios.post(`${API}/auth/register`, payload);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Account created! Your business documents will be verified by our team.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      
      if (response.data.debug_token) {
        const resetUrl = `${window.location.origin}/auth?token=${response.data.debug_token}`;
        toast.success(
          <div>
            <p className="font-medium">Reset link generated!</p>
            <p className="text-xs mt-1 text-muted-foreground">(Email not configured - showing link directly)</p>
            <a href={resetUrl} className="text-xs text-blue-600 underline mt-2 block break-all">
              Click here to reset password
            </a>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success('If an account exists with this email, a reset link has been sent');
      }
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setView('reset-success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid for submission - Updated for 3 steps (no video)
  const isFormValid = () => {
    if (!selectedCountry) return false;
    
    const hasVerifiedPhone = registerData.phone.verified;
    const hasAcceptedTerms = termsAccepted;
    const hasRequiredFields = registerData.email && registerData.password && registerData.full_name && registerData.company_name;
    
    // Check primary business ID if required
    const primaryField = countryConfig?.fields?.find(f => f.type === 'primary');
    const hasPrimaryId = !primaryField?.required || registerData.primary_business_id;
    
    // Check no validation errors
    const hasNoErrors = !Object.values(fieldErrors).some(err => err);
    
    return hasVerifiedPhone && hasAcceptedTerms && hasRequiredFields && hasPrimaryId && hasNoErrors;
  };

  // Check if step 1 (basic info) is complete
  const isStep1Complete = () => {
    return registerData.email && 
           registerData.password && 
           registerData.full_name && 
           registerData.company_name &&
           selectedCountry &&
           registerData.phone.verified;
  };

  // Check if step 2 (business ID) is complete
  const isStep2Complete = () => {
    const primaryField = countryConfig?.fields?.find(f => f.type === 'primary');
    const hasPrimaryId = !primaryField?.required || registerData.primary_business_id;
    const hasNoErrors = !Object.values(fieldErrors).some(err => err);
    return hasPrimaryId && hasNoErrors;
  };

  // Get primary business ID label for current country
  const getPrimaryBusinessIdLabel = () => {
    const primaryField = countryConfig?.fields?.find(f => f.type === 'primary');
    return primaryField?.label || 'Business Registration Number';
  };

  // Role Selection View
  if (view === 'role-select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <SEO {...SEO_CONFIGS.auth} />
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-12 w-auto" />
            </div>
            <p className="text-muted-foreground">Connect. Automate. Trade Globally.</p>
          </div>

          <Card data-testid="role-select-card">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl">Welcome to GlobalVyapar</CardTitle>
              <CardDescription>How would you like to use our platform?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Global Seller/Exporter Card */}
                <div 
                  onClick={() => setView('register-seller')}
                  className="border-2 border-slate-200 hover:border-green-500 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg group"
                  data-testid="select-exporter-btn"
                >
                  <div className="p-3 bg-green-100 rounded-xl w-fit mb-4 group-hover:bg-green-500 transition-colors">
                    <Package className="h-8 w-8 text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">I am a Seller / Exporter</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Manufacturer, trader, or supplier looking to sell products globally
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>List products & get discovered</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>AI-powered lead management</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Business verification for trust</span>
                    </li>
                  </ul>
                  <Button className="w-full mt-4 bg-green-600 hover:bg-green-700">
                    Register as Seller
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>

                {/* Global Buyer Card */}
                <div 
                  onClick={() => navigate('/buyer-signup')}
                  className="border-2 border-slate-200 hover:border-blue-500 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg group"
                  data-testid="select-buyer-btn"
                >
                  <div className="p-3 bg-blue-100 rounded-xl w-fit mb-4 group-hover:bg-blue-500 transition-colors">
                    <Globe className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">I am a Buyer</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Looking to source products from verified global exporters
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>Browse verified exporters worldwide</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>AI Smart Match for suppliers</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-500" />
                      <span>Business verification required</span>
                    </li>
                  </ul>
                  <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                    Get Verified as Buyer
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>

              <div className="text-center pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Already have an account?</p>
                <Button variant="outline" onClick={() => setView('login')} data-testid="go-to-login-btn">
                  Sign In to Your Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Login View
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <SEO {...SEO_CONFIGS.auth} />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-12 w-auto mx-auto mb-4" />
          </div>

          <Card data-testid="login-card">
            <CardHeader>
              <CardTitle className="font-heading">Sign In</CardTitle>
              <CardDescription>Welcome back! Sign in to your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setView('forgot-password')}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-muted-foreground mb-2">Don&apos;t have an account?</p>
                <Button variant="outline" onClick={() => setView('role-select')} className="w-full">
                  Create New Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot Password View
  if (view === 'forgot-password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-12 w-auto mx-auto mb-4" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Forgot Password</CardTitle>
              <CardDescription>Enter your email to receive a password reset link</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Send Reset Link'}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setView('login')}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset Password View
  if (view === 'reset-password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-12 w-auto mx-auto mb-4" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Reset Password</CardTitle>
              <CardDescription>Enter your new password below</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</> : 'Reset Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset Success View
  if (view === 'reset-success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">Password Reset Successful!</h3>
                <p className="text-muted-foreground mb-6">
                  Your password has been reset. You can now login with your new password.
                </p>
                <Button onClick={() => { setView('login'); navigate('/auth'); }}>
                  Go to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Global Seller/Exporter Registration View - Multi-Step Wizard (3 Steps)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <SEO {...SEO_CONFIGS.auth} />
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <img src="/logo-cropped.png" alt="GlobalVyapar" className="h-12 w-auto mx-auto mb-4" />
        </div>

        <Card data-testid="register-seller-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle className="font-heading">Seller Registration</CardTitle>
                <CardDescription>
                  Step {registrationStep} of 3: {
                    registrationStep === 1 ? 'Basic Information' :
                    registrationStep === 2 ? 'Business Verification' :
                    'Review & Submit'
                  }
                </CardDescription>
              </div>
            </div>
            
            {/* Progress Bar - Now 3 steps */}
            <div className="mt-4">
              <Progress value={(registrationStep / 3) * 100} className="h-2" />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span className={registrationStep >= 1 ? 'text-green-600 font-medium' : ''}>Basic Info</span>
                <span className={registrationStep >= 2 ? 'text-green-600 font-medium' : ''}>Business ID</span>
                <span className={registrationStep >= 3 ? 'text-green-600 font-medium' : ''}>Submit</span>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Step 1: Basic Information */}
            {registrationStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="register-email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@company.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="register-password">Password <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        type={showRegisterPassword ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="full-name">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="full-name"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company-name">Company Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="company-name"
                      value={registerData.company_name}
                      onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                      required
                      data-testid="register-company-input"
                    />
                  </div>
                </div>

                {/* Country Selection */}
                <div>
                  <Label className="flex items-center gap-1">
                    <Globe className="h-4 w-4" />
                    Country <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedCountry?.code || ''}
                    onValueChange={(code) => {
                      const country = countries.find(c => c.code === code);
                      setSelectedCountry(country);
                      setCountrySearch('');
                    }}
                    data-testid="country-select"
                  >
                    <SelectTrigger className="w-full" data-testid="country-select-trigger">
                      <SelectValue placeholder={loadingCountries ? "Loading..." : "Select your country"}>
                        {selectedCountry && (
                          <span className="flex items-center gap-2">
                            <span>{selectedCountry.flag}</span>
                            <span>{selectedCountry.name}</span>
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 pb-2">
                        <Input
                          placeholder="Search countries..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className="h-8"
                          data-testid="country-search-input"
                        />
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {filteredCountries.length === 0 ? (
                          <div className="py-2 px-3 text-sm text-muted-foreground">No countries found</div>
                        ) : (
                          filteredCountries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              <span className="flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.name}</span>
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phone Verification */}
                <PhoneVerification
                  value={registerData.phone}
                  onChange={(phone) => setRegisterData({ ...registerData, phone })}
                  onVerified={() => toast.success('Phone verified!')}
                  required
                />

                {/* District Selection - Only for India */}
                {selectedCountry?.code === 'IN' && (
                  <div>
                    <Label className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      District
                    </Label>
                    <DistrictSelect
                      value={registerData.district}
                      onChange={(district) => setRegisterData({ ...registerData, district })}
                      placeholder="Select your district"
                    />
                  </div>
                )}

                {/* Step 1 Validation Messages */}
                {!selectedCountry && (
                  <p className="text-xs text-amber-600 text-center">Please select your country to continue</p>
                )}
                {!registerData.phone.verified && (
                  <p className="text-xs text-amber-600 text-center">Please verify your phone number to continue</p>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setView('role-select')}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    type="button"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!isStep1Complete()}
                    onClick={() => setRegistrationStep(2)}
                    data-testid="step1-next-btn"
                  >
                    Next: Business ID
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Business ID Verification */}
            {registrationStep === 2 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-800 text-sm font-medium mb-1">
                    <Building className="h-4 w-4" />
                    Business Verification for {selectedCountry?.name}
                  </div>
                  <p className="text-xs text-blue-700">
                    Enter your mandatory business registration details. These will be verified by our team.
                  </p>
                </div>

                {loadingConfig ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading verification requirements...</span>
                  </div>
                ) : countryConfig && countryConfig.fields && countryConfig.fields.length > 0 ? (
                  <div className="space-y-4">
                    {countryConfig.fields.map((field) => (
                      <div key={field.field_id}>
                        <Label htmlFor={field.field_id}>
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id={field.field_id}
                          value={field.type === 'primary' ? registerData.primary_business_id : registerData.secondary_business_id}
                          onChange={(e) => handleBusinessFieldChange(
                            field.type === 'primary' ? 'primary_business_id' : 'secondary_business_id',
                            e.target.value
                          )}
                          placeholder={field.placeholder}
                          required={field.required}
                          data-testid={`${field.field_id}-input`}
                          className={
                            fieldErrors[field.type === 'primary' ? 'primary_business_id' : 'secondary_business_id'] 
                              ? 'border-red-500' 
                              : (field.type === 'primary' ? registerData.primary_business_id : registerData.secondary_business_id) && 
                                !fieldErrors[field.type === 'primary' ? 'primary_business_id' : 'secondary_business_id']
                                ? 'border-green-500' 
                                : ''
                          }
                        />
                        <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                        {fieldErrors[field.type === 'primary' ? 'primary_business_id' : 'secondary_business_id'] && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {fieldErrors[field.type === 'primary' ? 'primary_business_id' : 'secondary_business_id']}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                    <AlertCircle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                    <p className="text-sm text-amber-800 font-medium">Business verification required</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Please provide your business registration number for {selectedCountry?.name}.
                    </p>
                  </div>
                )}

                {/* Step 2 Validation */}
                {countryConfig?.fields?.find(f => f.type === 'primary')?.required && !registerData.primary_business_id && (
                  <p className="text-xs text-amber-600 text-center">
                    {getPrimaryBusinessIdLabel()} is required to continue
                  </p>
                )}

                {/* Navigation Buttons - Go to Review (Step 3) */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setRegistrationStep(1)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    type="button"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!isStep2Complete()}
                    onClick={() => setRegistrationStep(3)}
                    data-testid="step2-next-btn"
                  >
                    Next: Review
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Submit */}
            {registrationStep === 3 && (
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Review Summary */}
                <div className="space-y-3">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-medium text-slate-800 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Name:</span>
                        <p className="font-medium">{registerData.full_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <p className="font-medium">{registerData.email}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Company:</span>
                        <p className="font-medium">{registerData.company_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Country:</span>
                        <p className="font-medium">{selectedCountry?.flag} {selectedCountry?.name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-medium text-slate-800 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Business Verification
                    </h3>
                    <div className="text-sm">
                      <span className="text-muted-foreground">{getPrimaryBusinessIdLabel()}:</span>
                      <p className="font-medium font-mono">{registerData.primary_business_id || 'Not provided'}</p>
                    </div>
                  </div>

                  {/* Video Verification Notice - Will be done after signup */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                      <Video className="h-4 w-4" />
                      Video Verification (After Signup)
                    </h3>
                    <p className="text-sm text-blue-700">
                      To complete your profile and get verified, you&apos;ll need to submit a short video after creating your account. 
                      This helps build trust with buyers.
                    </p>
                  </div>
                </div>

                {/* Terms Acceptance */}
                <div className="bg-slate-50 border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="terms" 
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked)}
                      data-testid="terms-checkbox"
                    />
                    <div className="grid gap-1.5 leading-none">
                      <label htmlFor="terms" className="text-sm font-medium leading-tight cursor-pointer">
                        I accept the Terms of Use <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-muted-foreground">
                        By checking this box, I agree to GlobalVyapar&apos;s{' '}
                        <Link to="/terms-of-service" target="_blank" className="text-primary hover:underline">
                          Terms of Use
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Validation Messages */}
                {!termsAccepted && (
                  <p className="text-xs text-amber-600 text-center">
                    Please accept the Terms of Use to complete registration
                  </p>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setRegistrationStep(2)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={isLoading || !isFormValid()}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Create Seller Account
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
